import {
  ForwardRefRenderFunction,
  HTMLAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { LOCALE } from '@hhgtech/hhg-components/types'
import { Check } from '@hhgtech/icons/core'
import { Box, BoxProps, Flex, Stack, rem, useMantineTheme } from '@mantine/core'
import { useClickOutside } from '@mantine/hooks'
import flatten from 'lodash/flatten'
import sortBy from 'lodash/sortBy'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatTopServices } from 'api/dataTransform/carePath'
import { PATHS } from 'api/paths'
import { Container } from 'components/atoms/container'
import ArrowDown from 'components/atoms/icons/ArrowDown'
import Pin from 'components/atoms/icons/Pin'
import { CarouselOrListingBlockWrapper } from 'components/organisms/serviceConnections/carouselOrListingBlockWrapper'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { CARE_LOCALISED_SLUGS } from 'interfaces/constants'
import {
  CareDoctor,
  ServiceItem,
  SpotlightEmbeddingDoctorType,
  SpotlightEmbeddingDoctorsSession,
  TopServicesByCitiesApi,
} from 'interfaces/types'

import { Circle } from './circle'
import {
  CardItem,
  DoctorCard,
  DoctorCardDisplay,
  LayoutConfig,
} from './doctorCard'
import { HalfCircle } from './halfCircle'
import useStyles from './index.styles'
import { Shape } from './shape'
import { ViewAllBtn } from './viewAllBtn'
import { CarouselOrListingBlockProps } from '../carouselOrListingBlock'

export type ShapeTypeProps = {
  defaultColor?: string
} & HTMLAttributes<SVGElement>
type CarePathDoctorListProps = {
  defaultColor: string
  sessionProps: SpotlightEmbeddingDoctorsSession
  selectedFilterId?: string
  listingAll?: Array<CareDoctor | ServiceItem>
  hidePrice?: boolean
  hideViewAll?: boolean
  hideBgPattern?: boolean
  filterColor?: string
  showConfirmCityPopup?: boolean
  viewAllUrl?: string
  layoutConfig?: LayoutConfig
  showPromotionEvent?: boolean
  carouselListProps?: Partial<CarouselOrListingBlockProps>
  applyH1?: boolean
  campaign?: {
    name: string
    source: string
  }
} & BoxProps

export type DoctorsByGroupType = {
  group?: {
    name: string
    id: string
  }
  doctors: CareDoctor[] | ServiceItem[]
}

const formatProps = (data: any, type: SpotlightEmbeddingDoctorType) => {
  return type === 'top_service'
    ? {
        name: data.name,
        validOutpatient: false,
        validTelemedicine: false,
        rating: data.hospital.rating || '',
        doctorSpecialties: [],
        hospital: data.hospital.name || '',
        slug: data.slug,
        id: data.id,
        avatar: data.image || '',
        price: data.price || '',
        priceUnit: data.priceUnit || '',
        originalPrice: data.originalPrice || '',
        cityName: data.hospital.city?.name || '',
      }
    : {
        name: data.name,
        validOutpatient: data.validOutpatient,
        validTelemedicine: data.validTelemedicine,
        rating: data.rating,
        doctorSpecialties: data.doctorSpecialties,
        hospital: data.hospital.name || '',
        slug: data.slug,
        id: data.id,
        avatar: data.avatar,
        price: data.price || '',
        priceUnit: data.priceUnit || '',
        originalPrice: data.originalPrice || '',
        cityName: data.hospital.city?.name || '',
      }
}

export const SLUG_ALL_BY_LOALE: {
  [key in LOCALE]?: string
} = {
  [LOCALE.Vietnam]: 'tat-ca',
  [LOCALE.Indonesia]: 'semua',
}

export type HandlerProps = {
  setSelectedFilterId: (v: string) => void
}

const CarePathDoctorList: ForwardRefRenderFunction<
  HandlerProps,
  CarePathDoctorListProps
> = (
  {
    defaultColor,
    sessionProps,
    hidePrice,
    hideViewAll,
    hideBgPattern,
    listingAll = [],
    selectedFilterId,
    filterColor,
    styles,
    showConfirmCityPopup,
    viewAllUrl,
    layoutConfig,
    showPromotionEvent,
    carouselListProps,
    applyH1,
    campaign,
    ...boxProps
  },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const { classes } = useStyles(undefined, {
    name: 'CarePathDoctorList',
    styles,
  })
  const [listByGroup, setListByGroup] = useState<Array<DoctorsByGroupType>>([])
  const [value, setValue] = useState<string | null>(null)
  const [doctors, setDoctors] = useState<Array<CareDoctor | ServiceItem>>([])
  const {
    title,
    description,
    typeEmbedding,
    embebIds,
    backgroundColor: backgroundColorProp,
  } = sessionProps
  const backgroundColor = backgroundColorProp || defaultColor
  const theme = useMantineTheme()
  const shadeGap = 0.08
  const isMobile = useIsMobile()
  const hasFilter = useMemo(() => typeEmbedding === 'top_service', [
    typeEmbedding,
  ])
  const [openFakeDropdown, setOpenFakeDropdown] = useState(false)
  const filterRef = useClickOutside(() => setOpenFakeDropdown(false))

  useImperativeHandle(ref, () => ({
    setSelectedFilterId: (id: string) => setValue(id),
  }))

  useEffect(() => {
    hasFilter &&
      (async () => {
        const res = await callApiNew(
          PATHS.CARE.GET_TOP_SERVICES,
          {
            params: {
              showDistrict: 1,
              categoryIds: embebIds.join(','),
            },
          },
          'care',
        )
        setListByGroup(
          flatten(
            ((res?._data?.top_services?.city_list ||
              []) as TopServicesByCitiesApi[]).map((i) => ({
              group: {
                id: i.city?.id || '',
                name: i.city?.name || '',
              },
              doctors: sortBy(
                flatten(
                  formatTopServices(i).serviceList.map((j) => j.services),
                ),
                ['position'],
                'asc',
              ),
            })),
          ) as any,
        )
      })()
  }, [])

  useEffect(() => {
    hasFilter && selectedFilterId && setValue(selectedFilterId)
  }, [selectedFilterId])

  useEffect(() => {
    if (value === null || value === 'all') {
      setDoctors(listingAll)
    } else {
      setDoctors(
        flatten(
          listByGroup
            .filter((i) => i.group?.id == value)
            .map((i) => i.doctors as ServiceItem[]),
        ),
      )
    }
  }, [value])

  const viewAllByType = (specId?: string) =>
    `/care/${SLUG_ALL_BY_LOALE?.[locale] || 'all'}/${
      CARE_LOCALISED_SLUGS[locale]?.[
        typeEmbedding === 'top_service' ? 'SERVICE' : 'DOCTOR'
      ]
    }/${specId ? `?specialtyId=${specId}` : ''}`

  const viewAllLink =
    viewAllUrl ||
    `https://${domainLocales[locale]}${
      typeEmbedding === 'specialties' && embebIds.length === 1
        ? viewAllByType(embebIds?.[0])
        : viewAllByType()
    }`

  const viewAllProps = {
    href: viewAllLink,
    target: '_blank',
    'data-event-category': 'Service Connection',
    'data-event-action': 'View All Doctor Click',
    'data-event-label': viewAllLink,
  }
  const defaultLayoutConfig: LayoutConfig = {
    1: 'wide-horizon',
    2: 'horizon',
  }
  const layout: DoctorCardDisplay = isMobile
    ? 'vertical'
    : layoutConfig?.[doctors.length] ||
      defaultLayoutConfig?.[doctors.length] ||
      'vertical'

  const cardCommonProps = {
    className: 'item',
    isService: typeEmbedding === 'top_service',
    hidePrice,
    display: layout,
    showPromotionEvent,
    showConfirmCityPopup:
      showConfirmCityPopup && (value === 'all' || value === null),
    campaign,
    onCancel: () => setOpenFakeDropdown(true),
  }

  const filterOpts = useMemo(
    () => [
      {
        label: f({ id: 'keyword.all' }),
        value: 'all',
      },
      ...listByGroup.map((i) => ({
        label: i.group?.name,
        value: String(i.group?.id),
      })),
    ],
    [listByGroup],
  )

  const filterLableByVal = useMemo(() => {
    const tmp: { [key in string]: string } = {}
    filterOpts.map((i) => {
      if (i.value) {
        tmp[i.value] = i.label || ''
      }
    })

    return tmp
  }, [listByGroup])

  return doctors.length > 0 ? (
    <Box
      sx={(theme) => ({
        position: 'relative',
        background: backgroundColor,
        overflow: 'hidden',
        [theme.fn.smallerThan('sm')]: {
          '.desktop-btn': {
            display: 'none',
          },
        },
        [theme.fn.largerThan('sm')]: {
          '.mobile-btn': {
            display: 'none',
          },
        },
      })}
      {...boxProps}
    >
      {!hideBgPattern && (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          >
            <Circle color={theme.fn.darken(backgroundColor, shadeGap)} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
            }}
          >
            <HalfCircle color={theme.fn.lighten(backgroundColor, shadeGap)} />
            <Box
              sx={{
                position: 'absolute',
                left: rem(44),
                top: 0,
                transform: 'translate(0, -75%)',
              }}
            >
              <Shape color={theme.fn.darken(backgroundColor, shadeGap)} />
            </Box>
          </Box>
        </>
      )}
      <Container style={{ position: 'relative' }}>
        <Stack
          sx={(theme) => ({
            padding: `${rem(24)} 0`,
            gap: rem(24),
            [theme.fn.largerThan('sm')]: {
              padding: `${rem(40)} 0`,
              gap: rem(40),
            },
          })}
        >
          <Flex
            justify={'space-between'}
            sx={(theme) => ({
              [theme.fn.smallerThan('sm')]: {
                flexDirection: 'column',
                gap: rem(8),
              },
            })}
          >
            <Stack
              sx={(theme) => ({
                gap: rem(8),
                [theme.fn.largerThan('sm')]: {
                  gap: rem(12),
                },
              })}
            >
              {title && (
                <Text
                  size="h2"
                  as={applyH1 ? 'h1' : 'h2'}
                  color="#fff"
                  className={classes.heading}
                >
                  {title}
                </Text>
              )}
              {description && (
                <Text size="s3" color="#fff" weight="semiBold">
                  {description}
                </Text>
              )}
            </Stack>
            {hasFilter ? (
              <Box
                ref={filterRef}
                sx={{ position: 'relative', userSelect: 'none' }}
              >
                <Flex
                  className={classes.filterInput}
                  align={'center'}
                  justify={'space-between'}
                  onClick={() => setOpenFakeDropdown(!openFakeDropdown)}
                  sx={(theme) => ({
                    padding: `${rem(10)} ${rem(12)}`,
                    backgroundColor: '#fff',
                    borderRadius: rem(4),
                    border: `1px solid ${theme.colors.neutral[2]}`,
                    cursor: 'pointer',
                    width: rem(250),
                    ...(filterColor && {
                      backgroundColor: filterColor,
                      color: '#fff',
                    }),
                    [theme.fn.smallerThan('sm')]: {
                      width: '100%',
                    },
                  })}
                >
                  <Flex gap={rem(8)} align={'center'}>
                    <Pin
                      size={18}
                      color={filterColor ? '#fff' : theme.colors.neutral[4]}
                    />
                    <Box
                      sx={{
                        fontSize: rem(14),
                        lineHeight: rem(18),
                        ...(!value && {
                          color: filterColor ? '#fff' : theme.colors.neutral[7],
                        }),
                        ...(filterColor && {
                          fontWeight: 600,
                        }),
                      }}
                    >
                      {value
                        ? filterLableByVal[value]
                        : f({
                            id: 'carePathDoctorList.filter.placeholder',
                          })}
                    </Box>
                  </Flex>
                  <ArrowDown
                    size={18}
                    color={filterColor ? '#fff' : undefined}
                  />
                </Flex>
                <Box
                  sx={(theme) => ({
                    display: openFakeDropdown ? 'block' : 'none',
                    position: 'absolute',
                    top: rem(45),
                    width: '100%',
                    background: '#fff',
                    border: `1px solid ${theme.colors.gray[1]}`,
                    borderRadius: rem(4),
                    zIndex: 1,
                    padding: rem(2),
                    boxShadow: '1px 1px 4px 1px rgba(0,0,0,0.05)',
                  })}
                >
                  <Stack spacing={0}>
                    {filterOpts.map((i, idx) => {
                      const isSelected = i.value === value
                      return (
                        <Flex
                          justify={'space-between'}
                          key={String(idx)}
                          sx={(theme) => ({
                            padding: `${rem(8)} ${rem(16)}`,
                            '&:hover': {
                              background: theme.colors.gray[1],
                            },
                            ...(isSelected
                              ? {
                                  '&,&:hover': {
                                    backgroundColor: '#d5f1f1',
                                  },
                                }
                              : {
                                  cursor: 'pointer',
                                }),
                          })}
                          onClick={() => {
                            setValue(i.value)
                            setOpenFakeDropdown(false)
                          }}
                        >
                          <Text size={isMobile ? 'p3' : 'p4'}>{i.label}</Text>
                          {isSelected && (
                            <Check size={18} color={filterColor} />
                          )}
                        </Flex>
                      )
                    })}
                  </Stack>
                </Box>
              </Box>
            ) : !hideViewAll ? (
              <ViewAllBtn {...viewAllProps} className="desktop-btn" />
            ) : null}
          </Flex>
          <CarouselOrListingBlockWrapper
            showFullSlide={!isMobile}
            lengthToCarouselDesktop={5}
            gapDesktop={20}
            gapMobile={20}
            {...carouselListProps}
          >
            <CarouselOrListingBlockWrapper.Slider
              classNames={{
                listing: classes.listing,
              }}
            >
              {doctors.map((i, idx) => (
                <Box
                  key={String(idx)}
                  className={classes.cardWrapper}
                  sx={{ width: '100%' }}
                >
                  <DoctorCard
                    {...cardCommonProps}
                    item={formatProps(i, typeEmbedding) as CardItem}
                    sx={{ height: '100%' }}
                  />
                </Box>
              ))}
            </CarouselOrListingBlockWrapper.Slider>
          </CarouselOrListingBlockWrapper>
          {!hasFilter && !hideViewAll && (
            <ViewAllBtn {...viewAllProps} className="mobile-btn" />
          )}
        </Stack>
      </Container>
    </Box>
  ) : null
}

export default forwardRef(CarePathDoctorList)
